import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertCircle, FiArrowRight, FiBarChart3 } = FiIcons;

const ProblemSection = () => {
  const problems = [
    "You dominate practice but freeze under game pressure",
    "Overthinking every move instead of trusting your instincts",
    "You're part of the 35% of athletes dealing with performance anxiety, burnout, or mental blocks",
    "Chugging energy drinks and pre-workout just to feel sharp",
    "Making split-second mistakes that cost games and opportunities",
    "Coaches tell you to focus, but your mind races out of control",
    "Knowing you have the skills but can't access them when it matters"
  ];

  const performanceTrap = [
    "Elite skills",
    "Pressure situations", 
    "EF overload",
    "Mistakes",
    "More pressure"
  ];

  const stats = [
    {
      stat: "Elite athletes demonstrate superior executive functions compared to lower-skilled athletes",
      source: "PMC:11688047, 2024"
    },
    {
      stat: "Elite athletes outperform non-athletes on cognitive tasks",
      source: "PMC:11688047, 2024"
    },
    {
      stat: "70% of high-pressure failures come from cognitive errors, often due to mental fatigue and overload",
      source: "Frontiers in Sports and Active Living, 2023"
    },
    {
      stat: "Effective regulation of executive functions predicts better sports performance and resilience",
      source: "Nature Scientific Reports, 2025"
    }
  ];

  return (
    <section id="problem" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Main headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Why Your Brain Fumbles When It Counts
          </h2>
        </motion.div>

        {/* Sound Familiar Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-accent-magenta h-8 w-8" />
              Sound familiar?
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl"
                >
                  <SafeIcon icon={FiAlertCircle} className="text-red-500 h-5 w-5 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{problem}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 p-6 bg-accent-light/50 rounded-xl border border-accent-lavender/30">
              <p className="text-gray-800 leading-relaxed">
                Your executive functions—those mental skills that control focus, quick decisions, and performance under pressure—are letting you down when you need them most{' '}
                <span className="text-sm align-super text-gray-600">[Frontiers in Sports and Active Living, 2023]</span>.
              </p>
              <p className="text-gray-800 leading-relaxed mt-4">
                Athletes experience marked reductions in skill level under stress, but what if the problem isn't nerves or how much you practiced—it's untrained brain skills?
              </p>
            </div>
          </div>
        </motion.div>

        {/* Performance Trap Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 mb-8">
              The Performance Trap:
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8">
              {performanceTrap.map((step, index) => (
                <React.Fragment key={index}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    className="flex flex-col items-center"
                  >
                    <div className="bg-accent-magenta text-white px-6 py-3 rounded-xl font-semibold text-center min-w-[120px]">
                      {step}
                    </div>
                  </motion.div>
                  {index < performanceTrap.length - 1 && (
                    <SafeIcon icon={FiArrowRight} className="text-gray-400 h-6 w-6 hidden lg:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Key Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <SafeIcon icon={FiBarChart3} className="text-accent-gold h-8 w-8" />
              Key Stats You Need to Know:
            </h3>
            <div className="space-y-6">
              {stats.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-6 bg-gradient-to-r from-accent-light/30 to-accent-purple/20 rounded-xl border border-accent-lavender/30"
                >
                  <div className="bg-accent-magenta text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium leading-relaxed">
                      {item.stat}{' '}
                      <span className="text-sm align-super text-gray-600">[{item.source}]</span>
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;